import React, { Component } from "react";

import {
  CSidebar,
  CSidebarForm,
  CSidebarHeader,
  CSidebarNav,
  CSidebarNavItem,
  CSidebarNavDropdown,
  CSidebarNavTitle,
  CLink
} from "@coreui/react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { setMinimizeMenu, setMenu } from "../actions/menuActions";
import logo16 from "../assets/imgs/logo-16.png";
import logoAvaliacao16 from "../assets/imgs/logo-avaliacao-16.png";
import logoAvaliacao from "../assets/imgs/logo-avaliacao-branco.png";
import logoLivros16 from "../assets/imgs/logo-livros-16.png";
import logoLivros from "../assets/imgs/logo-livros-branco.png";
import logo from "../assets/imgs/logo.png";
import logoAlagoas from "../assets/imgs/logoAlagoas.png";
import URL from "../config/api";
import axios from "../config/axios";
import { hasGameficandusModule } from "../utils/accessSession";
import { filtrarPendentes } from "../utils/atividadesPendentes";

const propTypes = {
  children: PropTypes.node
};

const defaultProps = {};

/* Seção ou submenu sem nenhum item visível não aparece. O backend manda a
   seção quando o perfil a tem, mesmo que nenhum item dentro dela passe (perfil
   ou módulo do grupo) — sobrava só o título, como "ADMINISTRATIVO" vazio para
   o professor. Devolve cópias: general.menu é persistido pelo redux-persist. */
const semSecoesVazias = itens =>
  itens.reduce((visiveis, item) => {
    if (!Array.isArray(item.children)) return [...visiveis, item];
    const filhos = semSecoesVazias(item.children);
    return filhos.length
      ? [...visiveis, { ...item, children: filhos }]
      : visiveis;
  }, []);

class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pendingActivitiesUrl: "/classroom/me",
      pendingActivitiesCount: 0
    };
  }

  componentDidMount() {
    const isGamifiedStudent =
      this.props.general.active_roles.role_id === 7 &&
      hasGameficandusModule(this.props.user.user_modules);

    if (isGamifiedStudent) {
      this.fetchPendingActivitiesUrl();
      this.props.setMinimizeMenu(true);
    }
  }

  fetchPendingActivitiesUrl = async () => {
    try {
      const resp = await axios.get(`${URL.api}general/dashboard/user`, {
        headers: { Authorization: `Bearer ${this.props.user.token}` }
      });

      // O endpoint devolve todas as atividades do ano — entregues, encerradas e
      // as que ainda nem abriram. O badge é um chamado para agir, então só entra
      // nele o que o aluno consegue fazer agora (ver `atividadesPendentes`).
      const atividades = resp.data.data.activities || [];
      const pendentes = filtrarPendentes(atividades);

      // Sem pendente, o link ainda leva às atividades da turma do aluno (as
      // entregues e encerradas ficam lá); sem atividade nenhuma, às turmas.
      // Antes caía no /dashboard, e o atalho "Atividades" parecia quebrado.
      const turmaId = (pendentes[0] || atividades[0])?.room_id;

      this.setState({
        pendingActivitiesCount: pendentes.length,
        pendingActivitiesUrl: turmaId
          ? `/classroom/${turmaId}/activities`
          : "/classroom/me"
      });
    } catch (error) {
      // mantém o fallback para /classroom/me
    }
  };

  removeMenuItem = (menu, menuName, childrenItemName) => {
    const removeChild = (children, name) =>
      children.filter(child => {
        if (child.children) {
          child.children = removeChild(child.children, name);
        }
        return child.name !== name;
      });

    const removeItem = (menu, menuName, childrenItemName) =>
      menu.map(item => {
        if (item.name === menuName && item.children) {
          item.children = removeChild(item.children, childrenItemName);
        } else if (item.children) {
          item.children = removeItem(item.children, menuName, childrenItemName);
        }
        return item;
      });

    return removeItem(menu, menuName, childrenItemName);
  };

  render() {
    let menu = this.props.general.menu.filter(item => item.name !== "Home");

    let userGroup = this.props.general.active_roles.group_id;

    const isStudent = this.props.general.active_roles.role_id === 7; // role_id 7 aluno.
    const isGamifiedStudent =
      isStudent && hasGameficandusModule(this.props.user.user_modules);

    // Os outros atalhos do aluno gamificado (Minhas turmas, Formulário,
    // Avaliando Conhecimentos, Minhas Conquistas) vêm do banco, soltos no topo
    // com module_id 62. "Atividades" fica aqui porque a url (1ª atividade
    // pendente) e o badge são calculados na hora; entra logo depois de
    // "Minhas turmas", como sempre esteve.
    if (isGamifiedStudent) {
      const atividades = {
        name: "Atividades",
        url: this.state.pendingActivitiesUrl,
        icon: "fa fa-clipboard",
        badge:
          this.state.pendingActivitiesCount > 0
            ? {
                text: String(this.state.pendingActivitiesCount),
                color: "danger",
                shape: "pill"
              }
            : undefined
      };
      const minhasTurmasIndex = menu.findIndex(
        item => item.url === "/classroom/me" && !item.children
      );
      menu.splice(minhasTurmasIndex + 1, 0, atividades);
    }

    const show = this.props.general.showMenu;
    const minimize = this.props.general.minimizeMenu;

    if (
      this.props.user.user_roles.filter(
        role => role.group_id === 61 || role.group_id === 62
      ).length === 0
    ) {
      const ajudaIndex = menu.findIndex(item => item.name === "Ajuda");
      const stiftIndex = menu[ajudaIndex]?.children?.findIndex(
        item => item.name === "Stift"
      );

      if (stiftIndex > -1) {
        menu[ajudaIndex].children.splice(stiftIndex, 1);
      }
    }

    let imgLogo = logo;
    let imgLogo16 = logo16;

    if (
      [
        "ava.livros.homologa.educandus.net",
        "ava.livros.educandus.net"
      ].includes(window.location.host.toLowerCase())
    ) {
      imgLogo = logoLivros;
      imgLogo16 = logoLivros16;
    } else if (
      [
        "ava.avaliacao.homologa.educandus.net",
        "ava.avaliacao.educandus.net"
      ].includes(window.location.host.toLowerCase())
    ) {
      imgLogo = logoAvaliacao;
      imgLogo16 = logoAvaliacao16;
    }

    if (userGroup === 86 && this.props.user.user_roles[0].role_id === 6) {
      menu.forEach(menuItem => {
        if (menuItem.children) {
          menuItem.children = menuItem.children.filter(
            child => child.id !== 60 && child.id !== 62
          );
        }
      });
    }

    menu = semSecoesVazias(menu);

    const isDigitalBooks = userGroup === 71;
    if (!isDigitalBooks) {
      return (
        <CSidebar
          id={
            this.props.general.active_roles.group_name === "Alagoas"
              ? "alagoas"
              : "menuAlterado"
          }
          show={show}
          minimize={minimize}
          onShowChange={show => this.props.setMenu(show)}
          onMinimizeChange={minimize => this.props.setMinimizeMenu(minimize)}
        >
          <CSidebarHeader>
            <CLink to="/dashboard">
              {this.props.user.user_roles[0].group_id === 49 ||
              this.props.user.user_roles[0].group_id === 86 ? (
                <img src={logoAlagoas} alt="Alagoas Logo" width="200px" />
              ) : (
                <img src={imgLogo} alt="Educandus Logo" />
              )}
            </CLink>
          </CSidebarHeader>
          <CSidebarForm />
          <CSidebarNav>
            {minimize && (
              <CSidebarNavItem
                id={
                  this.props.general.active_roles.group_name === "Alagoas"
                    ? "alagoas"
                    : ""
                }
              >
                <CLink to="/dashboard" className="c-sidebar-nav-link">
                  <i className="c-sidebar-nav-icon navbar-icon h-auto">
                    <img src={imgLogo16} alt="Educandus Logo" />
                  </i>
                  Página inicial
                </CLink>
              </CSidebarNavItem>
            )}
            {menu.map((title, index) =>
              title.children ? (
                <React.Fragment key={index}>
                  <CSidebarNavTitle>{title.name}</CSidebarNavTitle>
                  {title.children.map((item, index) =>
                    item.children ? (
                      <CSidebarNavDropdown
                        key={index}
                        name={item.name}
                        to={item.url}
                        style={{ fontSize: "14px" }}
                        fontIcon={`${item.icon} navbar-icon`}
                        id={
                          this.props.general.active_roles.group_name ===
                          "Alagoas"
                            ? "alagoas"
                            : ""
                        }
                      >
                        {item.children.map((child, index) => (
                          <CSidebarNavItem
                            key={index}
                            name={child.name}
                            to={child.url}
                            style={{ fontSize: "13px" }}
                            id={
                              this.props.general.active_roles.group_name ===
                              "Alagoas"
                                ? "alagoas"
                                : ""
                            }
                          />
                        ))}
                      </CSidebarNavDropdown>
                    ) : (
                      <CSidebarNavItem
                        key={index}
                        name={item.name}
                        style={{ fontSize: "14px" }}
                        to={item.url}
                        fontIcon={`${item.icon} navbar-icon`}
                        id={
                          this.props.general.active_roles.group_name ===
                          "Alagoas"
                            ? "alagoas"
                            : ""
                        }
                      />
                    )
                  )}
                </React.Fragment>
              ) : (
                <CSidebarNavItem
                  key={index}
                  name={title.name}
                  to={title.url}
                  fontIcon={`${title.icon} navbar-icon`}
                  badge={title.badge}
                  id={
                    this.props.general.active_roles.group_name === "Alagoas"
                      ? "alagoas"
                      : ""
                  }
                />
              )
            )}
          </CSidebarNav>
        </CSidebar>
      );
    }
  }
}

Sidebar.propTypes = propTypes;
Sidebar.defaultProps = defaultProps;

const mapStateToProps = state => ({
  general: state.general,
  user: state.user
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ setMinimizeMenu, setMenu }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
